<template>
  <div class="result-tabs">
    <button v-for="(tab, index) in tabs" :key="index" :ref="`tab-${index}`"
      :class="['tab', { 'is-active': isActiveTab(index) }, { 'is-disabled': !isMember }]"
      @click="switchResultView(index)" @keydown.enter.space.prevent="switchResultView(index)">
      {{ tab.label }}
    </button>
  </div>
  <div class="result-content">
    <h1>{{ tabs[activeTabIndex].label }}</h1>
    <div class="result-container">
      <div class="column" :class="{ 'benefit-layout': isBenefitsView, 'grography-layout': isGeographyView }">
        <div class="header">
          <h2 :class="{ 'font-size-20': !isTypicalSalaryView }" v-html="tabs[activeTabIndex].title"></h2>
          <div class="salary" v-if="isTypicalSalaryView">
            <span class="value">{{ salaryTotal }}</span>
            <span class="post-fix">
              {{ d['Salary.Result.SalaryTotalPostfix']?.item2 }}
            </span>
          </div>
          <SalaryAccordion v-if="isTypicalSalaryView" />
          <div v-else-if="isBenefitsView">
            <BenefitPercentageBox />
          </div>
          <div v-else-if="isGeographyView">
            <BenefitGeographyBox />
          </div>
        </div>
      </div>
      <div v-if="isTypicalSalaryView" class="column">
        <SalarySlider />
        <transition>
          <SalaryAccordion :secondary="true" :quartile="currentQuartile"
            :class="{ invisible: !displaySecondarySalaryAccordion }" />
        </transition>
      </div>
    </div>

    <div class="result-bottom">
      <button class="button button-calculate" @click="recalc()">
        {{ d['Salary.Recalculate.CTA']?.item2 }}
      </button>

      <div class="link-container">
        <a v-for="(link, key) in links" :key="key" :href="getLinkUrl(link)" :target="link.target" class="link">
          <svg class="arrow-icon" aria-hidden="true">
            <use :href="`${spritemap}#arrow-in-circle`"></use>
          </svg>
          <span class="text">{{ getLinkText(link) }}</span></a>

        <button v-if="isMember" @click="openModalForUserEmail()" class="link">
          <svg class="arrow-icon" aria-hidden="true">
            <use :href="`${spritemap}#arrow-in-circle`"></use>
          </svg>
          <span class="text">{{ d['Salary.ModalEmailToUser.OpenModalButton']?.item2 }}</span>
        </button>

        <button v-if="isMember && isJuraMailActive" @click="openModalForJuraEmail()" class="link">
          <svg class="arrow-icon arrowlink" aria-hidden="true">
            <use :href="`${spritemap}#arrow-in-circle`"></use>
          </svg>
          <span class="text">{{ d['Salary.ModalFeedback.OpenModalButton']?.item2 }}</span>
        </button>
      </div>
    </div>

    <BenefitBox v-if="!isMember" :title="memberBenefitsTitle" :subtitle="memberBenefitsSubtitle"
      :benefits="memberBenefits" theme="green" :link="benefitBoxLink" />
  </div>
  <Modal ref="modalUnauthenticated" :title="d['Salary.Result.ModalUnauthenticatedTitle']?.item2"
    :description="d['Salary.Result.ModalUnauthenticatedText']?.item2" :has-close-button="true" :has-info-button="false"
    @close="handleModalClose" class="absolute">
    <template v-slot>
      <button class="button button-modal" @click="this.$refs.modalUnauthenticated.toggleModal">
        {{ d['Salary.Result.ModalUnauthenticatedButtonText']?.item2 }}
      </button>
    </template>
  </Modal>
  <Modal ref="modalEmailOrComment" :title="modalFeedbackTitleText" :description="modalFeedbackDescriptionText"
    :has-close-button="true" :has-info-button="false" @close="handleModalClose" class="absolute">
    <template v-slot>
      <LoadingSuccess v-if="isEmailSent" />
      <div class="form-container">
        <div v-if="!isEmailSent && modalType === 'juraEmail'" class="input-container comment-field">
          <label for="feedback">{{ d['Salary.ModalFeedback.LabelTextField']?.item2 }}</label>
          <textarea id="feedback" ref="feedback" />
          <span class="error-text">{{ d['Salary.ModalFeedback.ErrorTextField']?.item2 }}</span>
        </div>
        <div v-if="!isEmailSent" class="input-container required">
          <label for="comment-email">{{ modalType === 'juraEmail' ? d['Salary.ModalFeedback.LabelEmail']?.item2 :
            d['Salary.ModalFeedback.LabelEmailUser']?.item2 }}</label>
          <input class="comment-email-input" type="email" id="comment-email" v-model="userEmail" ref="email" />
          <span class="error-text">{{ d['Salary.LeadModal.PrivateEmail.Error']?.item2 }}</span>
        </div>

      </div>
      <div class="submit-container">
        <span v-if="!isEmailSent" class="error-text" ref="submitErrorText">
          {{ d['Salary.ModalFeedback.ErrorSubmit']?.item2 }}
        </span>
        <button v-if="!isEmailSent" @click="modalType === 'juraEmail' ? sendMailToJura() : sendMailtoUser()"
          class="button button-modal" :disabled="isSubmittingEmail" :class="{ 'loading-state': isSubmittingEmail }">
          <span v-if="modalType === 'juraEmail'" v-show="!isSubmittingEmail">{{
            d['Salary.ModalFeedback.TextButton']?.item2
          }}</span>
          <span v-if="modalType === 'userEmail'" v-show="!isSubmittingEmail">{{
            d['Salary.ModalEmailToUser.TextButton']?.item2
          }}</span>
          <Transition>
            <LoadingSpinner v-if="isSubmittingEmail" />
          </Transition>
        </button>
      </div>
    </template>
  </Modal>
</template>
<script>
import { state, localDictionary } from '@/lib/state'
import { highlightText } from '@/util/stringUtil'
import { QUARTILE_MAP } from '@/lib/mapping'
import { svgSpritemapSrc } from '@/lib/mapping'
import BenefitBox from '@/components/BenefitBox.vue'
import SalaryAccordion from '@/components/SalaryAccordion.vue'
import BenefitPercentageBox from '@/components/BenefitPercentageBox.vue'
import BenefitGeographyBox from './BenefitGeographyBox.vue'
import SalarySlider from './SalarySlider.vue'
import Modal from '@/components/Modal.vue'
import { validateField } from '../../../../services/service-field-validation'
import { sendEmailToJura, sendEmailToUser } from '@/services/user-data-service.js'
import LoadingSpinner from '@/components/loading/LoadingSpinner.vue'
import LoadingSuccess from '@/components/loading/LoadingSuccess.vue'

export default {
  components: {
    BenefitBox,
    BenefitPercentageBox,
    BenefitGeographyBox,
    SalarySlider,
    SalaryAccordion,
    Modal,
    LoadingSpinner,
    LoadingSuccess,
  },
  data: function () {
    return {
      memberBenefitsTitle: state.resultMemberBenefitsTitle,
      memberBenefitsSubtitle: state.resultMemberBenefitsSubtitle,
      memberBenefits: state.resultMemberBenefits,

      d: localDictionary(),
      modalFeedbackTitle: {
        successJura: localDictionary()['Salary.ModalFeedback.Success']?.item2 || '',
        successUser: localDictionary()['Salary.ModalEmailToUser.SuccessTitle']?.item2 || '',
        error: localDictionary()['Salary.ModalFeedback.ErrorSubmit']?.item2 || '',
        jura: localDictionary()['Salary.ModalFeedback.Title']?.item2 || '',
        user: localDictionary()['Salary.ModalEmailToUser.Title']?.item2 || '',
      },
      modalFeedbackDescription: {
        successJura: localDictionary()['Salary.ModalFeedback.SuccessDescription']?.item2 || '',
        successUser: localDictionary()['Salary.ModalEmailToUser.SuccessDescription']?.item2 || '',
        error: localDictionary()['Salary.ModalFeedback.ErrorSubmit']?.item2 || '',
        jura: localDictionary()['Salary.ModalFeedback.Text']?.item2 || '',
        user: localDictionary()['Salary.ModalEmailToUser.Text']?.item2 || '',
      },
      activeFeedbackTitleKey: 'default', // Initialize with default titl
      spritemap: svgSpritemapSrc,
      activeTabIndex: 0,
      isSubmittingEmail: false,
      isEmailSent: false,
      modalType: '',
      tabs: [
        {
          label: localDictionary()['Salary.Result.TypicalSalary']?.item2,
          title: highlightText(state.resultTypicalSalaryTitle),
        },
        {
          label: localDictionary()['Salary.Result.Benefits']?.item2,
          title: highlightText(state.resultBenefitsTitle),
        },
        {
          label: localDictionary()['Salary.Result.Geography']?.item2,
          title: highlightText(state.resultGeographyTitle),
        },
      ],
    }
  },
  computed: {
    isMember() {
      return state.user.isMember
    },
    currentQuartile() {
      return QUARTILE_MAP[state.salarySliderIndex]?.key || 'q2'
    },
    modalFeedbackTitleText() {
      return this.modalFeedbackTitle[this.activeFeedbackTitleKey] || ''
    },
    modalFeedbackDescriptionText() {
      return this.modalFeedbackDescription[this.activeFeedbackTitleKey] || ''
    },
    salaryTotal() {
      const value = state.resultData?.q2
      if (!value) return ` ${this.labelCurrency}`

      const formatted = new Intl.NumberFormat(state.culture).format(value)
      return `${formatted} ${this.labelCurrency}`
    },
    labelCurrency() {
      return this.d['Salary.Result.LabelCurrency']?.item2 || ''
    },
    isTypicalSalaryView() {
      return this.activeTabIndex === 0
    },
    isBenefitsView() {
      return this.activeTabIndex === 1
    },
    isGeographyView() {
      return this.activeTabIndex === 2
    },
    displaySecondarySalaryAccordion() {
      return this.isTypicalSalaryView && state.isSalarySliderStarted
    },
    links() {
      return this.isMember ? state.resultLinks : state.resultLinksNonMember
    },
    userEmail: {
      get() {
        debugger;
        // Check which email is marked as primary
        if (state.user.privateEmail?.primary) {
          return state.user.privateEmail.email
        } else if (state.user.workEmail?.primary) {
          return state.user.workEmail.email
        }
        // Fallback to privateEmail if no primary is set
        return state.user.privateEmail?.email || state.user.workEmail?.email || ''
      },
      set(val) {
        // Update the primary email
        if (state.user.privateEmail?.primary) {
          state.user.privateEmail.email = val
        } else if (state.user.workEmail?.primary) {
          state.user.workEmail.email = val
        } else {
          // Default to updating privateEmail if no primary is set
          if (!state.user.privateEmail) {
            state.user.privateEmail = { email: '', primary: false }
          }
          state.user.privateEmail.email = val
        }
      },
    },
    benefitBoxLink() {
      return state.resultMemberBenefitsLink
    },
    isJuraMailActive() {
      return !!state?.juraMailActive
    },
  },
  mounted() {
    // Ensure juraMailActive is being tracked for reactivity
    console.log('Jura mail active:', this.isJuraMailActive)
  },
  methods: {
    openModalForUserEmail() {
      this.modalType = 'userEmail'
      this.activeFeedbackTitleKey = 'user'
      this.$refs.modalEmailOrComment.toggleModal()
    },
    openModalForJuraEmail() {
      this.modalType = 'juraEmail'
      this.activeFeedbackTitleKey = 'jura'
      this.$refs.modalEmailOrComment.toggleModal()
    },
    isActiveTab(index) {
      return this.activeTabIndex === index
    },
    recalc() {
      this.emitter.emit(`recalc-questions`, true)
    },
    switchResultView(index) {
      if (index > 0 && !this.isMember) {
        return this.$refs.modalUnauthenticated.toggleModal()
      }
      this.activeTabIndex = index
      this.$nextTick(() => {
        this.$refs[`tab-${index}`][0].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      })
    },

    async sendMailToJura() {
      if (this.isSubmittingEmail) return // Prevent multiple clicks

      const emailValue = this.$refs.email.value
      const emailContainer = this.$refs.email.parentElement
      const feedbackValue = this.$refs.feedback.value.trim()

      let isValid = validateField(emailContainer)

      // Check if feedback is empty
      if (feedbackValue.length === 0) {
        isValid = false
        this.$refs.feedback.parentElement.classList.add('error')
      } else {
        this.$refs.feedback.parentElement.classList.remove('error')
      }

      // If validation fails, return early without submitting
      if (!isValid) {
        return
      }

      this.isSubmittingEmail = true // Start loading state

      try {
        const response = await sendEmailToJura({
          memberId: state.user.memberId,
          email: emailValue,
          culture: state.culture,
          pdfValues: {
            date: '',
            education: state.displayAnswers['Salary.Questions.HighestEducation'] || '',
            age: state.displayAnswers['Salary.Questions.YourAge'] || 0,
            sector: state.displayAnswers['Salary.Questions.Sector'] || '',
            region: state.displayAnswers['Salary.Questions.Region'] || '',
            supervisoryResponsibility: state.displayAnswers['Salary.Questions.Leadership'] || '',
            workArea: state.displayAnswers['Salary.Questions.WorkArea'] || '',
            jobTitle: state.displayAnswers['Salary.Questions.WorkFunction'] || '',
            hoursPerWeek: state.displayAnswers['Salary.Questions.HoursAWeek'] || 0,
            salaryEstimateTotal: state.resultData?.q2 || 0,
            baseSalaryInclAllowances: state.resultData?.baseSalary || 0,
            pensionContributions: state.resultData?.pension || 0,
            variablePay: state.resultData?.irregularPayments || 0,
            specialHolidayAllowance: state.resultData?.holidayAllowance || 0,
            earnsLess25Percentile: state.resultData?.q1 || 0,
            earnsLess50Percentile: state.resultData?.q2 || 0,
            earnsLess75Percentile: state.resultData?.q3 || 0,
            earnsLess10Percentile: state.resultData?.q4 || 0,
          },
          firstName: state.user.firstName || '',
          comment: feedbackValue,
        })
        if (response.status >= 200 && response.status < 300) {
          // Call handleModalFeedback with success status and the title from modalFeedbackTitle

          this.handleModalFeedback(true, 'successJura')
        } else {
          // Call handleModalFeedback with error status and title
          this.$refs.submitErrorText.style.display = 'block'
        }
      } catch (error) {
        // Handle error
        console.error('Error posting data:', error)
        this.$refs.submitErrorText.style.display = 'block'
      } finally {
        this.isSubmittingEmail = false
        console.log('Feedback submission completed.')
      }
    },
    async sendMailtoUser() {
      if (this.isSubmittingEmail) return // Prevent multiple clicks

      const emailValue = this.$refs.email.value
      const emailContainer = this.$refs.email.parentElement
      //const feedbackValue = this.$refs.feedback.value

      let isValid = validateField(emailContainer)
      if (!isValid) return

      this.isSubmittingEmail = true // Start loading state

      try {
        const response = await sendEmailToUser({
          sendCalculation: true,
          memberId: state.user.memberId,
          email: emailValue,
          culture: state.culture,
          pdfValues: {
            date: '',
            education: state.displayAnswers['Salary.Questions.HighestEducation'] || '',
            age: state.displayAnswers['Salary.Questions.YourAge'] || 0,
            sector: state.displayAnswers['Salary.Questions.Sector'] || '',
            region: state.displayAnswers['Salary.Questions.Region'] || '',
            supervisoryResponsibility: state.displayAnswers['Salary.Questions.Leadership'] || '',
            workArea: state.displayAnswers['Salary.Questions.WorkArea'] || '',
            jobTitle: state.displayAnswers['Salary.Questions.WorkFunction'] || '',
            hoursPerWeek: state.displayAnswers['Salary.Questions.HoursAWeek'] || 0,
            salaryEstimateTotal: state.resultData?.q2 || 0,
            baseSalaryInclAllowances: state.resultData?.baseSalary || 0,
            pensionContributions: state.resultData?.pension || 0,
            variablePay: state.resultData?.irregularPayments || 0,
            specialHolidayAllowance: state.resultData?.holidayAllowance || 0,
            earnsLess25Percentile: state.resultData?.q1 || 0,
            earnsLess50Percentile: state.resultData?.q2 || 0,
            earnsLess75Percentile: state.resultData?.q3 || 0,
            earnsLess10Percentile: state.resultData?.q4 || 0,
          },
          firstName: state.user.firstName || '',
          comment: '',
        })
        if (response.status >= 200 && response.status < 300) {
          this.handleModalFeedback(true, 'successUser')
        } else {
          this.$refs.submitErrorText.style.display = 'block'
        }
      } catch (error) {
        console.error('Error posting data:', error)
        this.$refs.submitErrorText.style.display = 'block'
      } finally {
        this.isSubmittingEmail = false
        console.log('User email submission completed.')
      }
    },
    handleModalFeedback(isSuccess, titleKey) {
      this.isEmailSent = isSuccess
      this.activeFeedbackTitleKey = titleKey
    },
    handleModalClose() {
      this.activeFeedbackTitleKey = 'default' // Reset title
      this.isEmailSent = false // Reset form submission state
      this.isSubmittingEmail = false // Reset loading state

      if (this.$refs.submitErrorText) this.$refs.submitErrorText.style.display = 'none' // Hide error message text
    },

    getLinkUrl(link) {
      return link.route?.path || link.url
    },
    getLinkText(link) {
      return link.title || link.url
    },
  },
}
</script>
<style lang="postcss" scoped>
.result-tabs {
  display: flex;
  position: relative;
  gap: 8px;
  overflow-x: scroll;
  width: calc(100% + var(--grid-gutter-mobile) * 2);
  left: calc(var(--grid-gutter-mobile) * -1);
  padding: 0 var(--grid-gutter-mobile);
  margin-bottom: var(--grid-gutter-mobile);

  @media (min-width: 450px) {
    overflow-x: initial;
  }

  .tab {
    background: var(--color-grey-3);
    color: var(--color-black);
    font-weight: var(--font-weight-bold);
    width: 132px;
    height: 38px;
    padding: 8px;
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    border-radius: 60px;
    flex-shrink: 0;
    letter-spacing: 0.03em;
    font-family: var(--font-it-ex);

    &.is-active {
      color: var(--color-white);
      background: var(--color-blue);
    }

    &.is-disabled {
      color: var(--color-white);
    }
  }
}

.result-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;

  h1 {
    font-size: 42px;
    font-weight: 500;
    font-family: var(--font-it-ex);
    text-transform: none;
    color: var(--color-blue);
  }

  .result-container {
    display: flex;
    flex-direction: column;
    height: 100%;

    .column {
      flex-basis: 100%;
      padding: 28px 8px 12px 8px;
      height: fit-content;

      .header {
        text-align: center;

        .font-size-20 {
          font-size: 20px;
          padding-bottom: 20px;
        }

        h2 {
          text-transform: none;
        }
      }

      h2 {
        font-family: var(--font-it-ex);
        font-size: var(--font-size-body);
      }

      &:first-of-type {
        background-color: var(--color-blue);
        color: var(--color-white);
        border-radius: 16px;
        z-index: 1;
      }

      /* TODO: is this a new color that needs a variable? */
      &:nth-of-type(2) {
        background-color: #f4f8ff;
        color: var(--color-blue);
        border-radius: 16px;
        margin-top: -10px;
      }
    }

    /* TODO: get media queries from variables.css */
    @media (min-width: 1024px) {
      flex-direction: row;
      column-gap: var(--grid-gutter-2);

      .column {
        width: calc(50% - var(--grid-gutter-2) / 2);
        padding: 28px 40px 28px 28px;
        height: auto;

        &.benefit-layout {
          flex-basis: 100%;
          max-width: 100%;
          gap: var(--grid-gutter-2);
        }

        &.grography-layout {
          flex-basis: 100%;
          max-width: 50%;
          gap: var(--grid-gutter-2);
        }

        .header {
          text-align: left;
        }

        &:last-of-type {
          margin-top: 0;
          z-index: 0;
        }

        .invisible {
          visibility: hidden;
        }
      }
    }
  }
}

.salary {
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: baseline;
  }

  .value {
    font-family: var(--font-it);
    font-size: var(--font-size-header-large);
    font-weight: var(--font-weight-regular);
    line-height: 1;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  .post-fix {
    padding-left: 10px;
    font-size: var(--font-size-small);
    line-height: 1;
    font-weight: var(--font-weight-normal);
    font-family: var(--font-it-ex);
  }
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 28px;

  .comment-email-input {
    margin-bottom: 0 !important;
  }

  .comment-field {
    order: 3;

    @media (min-width: 1024px) {
      order: initial;
    }
  }
}

.button-modal {
  /* override .button styles */
  position: relative;
  display: flex;
  gap: 8px;
  align-self: center;
  transition: all 0.5s ease;
}

.result-bottom {
  display: flex;
  flex-direction: column;

  .button-calculate {
    width: max-content;
    align-self: center;
  }

  .link-container {
    margin: 30px 0;
    display: flex;
    flex-direction: column;
    gap: 13px;

    .link {
      font-family: var(--font-it-ex);
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-bold);
      display: flex;
      align-items: center;
      gap: 17px;
      width: fit-content;
      color: var(--color-blue);

      .arrow-icon {
        width: 26px;
        height: 26px;
        flex-shrink: 0;
        transition: all var(--ani-speed-2) var(--ani-type-1);
      }

      .text {
        text-align: left;
      }
    }

    .link:hover,
    .link:focus {
      .arrow-icon {
        transform: translateX(6px);
      }
    }
  }
}

@media (min-width: 1024px) {
  .result-bottom {
    flex-direction: row;
    justify-content: space-between;
    margin: calc((var(--grid-gutter-2) - 16px)) 0;
    gap: var(--grid-gutter-3);

    .link-container {
      flex-direction: row;
      column-gap: var(--grid-gutter-2);
      row-gap: var(--grid-gutter-mobile);
      margin: 0;
      flex-wrap: wrap;
    }
  }

  .button-calculate {
    align-self: flex-start;
  }

  :deep(.benefit-box) {
    display: grid;
    column-gap: 108px;
    row-gap: 62px;
    grid-template-columns: 386px 1fr;
    padding-bottom: 40px;

    .header,
    .button {
      grid-column: 1;
    }

    .benefit-list {
      grid-column: 2;
      grid-row: 1 / 3;
    }

    .button {
      align-self: flex-end;
    }
  }
}

/* Feedback modal */
.submit-container {
  align-self: center;
  display: flex;
  flex-direction: column;

  .button {
    width: fit-content;
  }
}

input#email {
  margin-bottom: 0;
}

textarea#feedback {
  height: 216px;
  margin-bottom: 0;
}

.input-container {
  &.error .error-text {
    display: block;
  }
}

.error-text {
  display: none;
  font-family: var(--font-it-ex);
  color: var(--color-darkred);
  font-size: 0.875rem;
}

.checkbox {
  margin-bottom: 0;
}

.v-leave-to {
  opacity: 0;
}

.absolute {
  position: absolute;
}

.v-enter-active,
.v-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}
</style>
